import { useState, useEffect, useCallback, useRef } from "react"
import { supabase } from "@/lib/supabase/client"
import { isMockMode } from "@/lib/mock/mock-supabase"
import { useAuth } from "@/hooks/useAuth"

/**
 * Conta total de mensagens não-lidas para o user logado.
 *
 * Modos:
 *  - Sem `tenantId` (null/undefined): conta de TODOS os tenants em que o user é
 *    membro. Comportamento original da task #149.
 *  - Com `tenantId`: conta APENAS conversas desse tenant. Task #161 — vendedor
 *    com uma unidade selecionada quer ver só notificações daquela unidade.
 *
 * Backend: RPC `count_unread_per_conversation_for_user()` (migration 100)
 * retorna lista de (conversation_id, unread_count) — somamos client-side.
 * Quando há tenantId, fazemos uma query auxiliar pra pegar os
 * conversation_ids do tenant e filtramos o resultado da RPC pra somar
 * apenas esses. Decisão arquitetural (PM 2026-05-21): manter a RPC
 * intacta e fazer o filtro client-side, evitando migration SQL.
 *
 * Realtime: subscribe à tabela `credit_v2_messages` (INSERT) e
 * `credit_v2_conversation_members` (* do user). Quando muda algo, refetch.
 */
export function useUnreadMessagesCount(
  tenantId?: string | null,
): { unreadCount: number; refetch: () => Promise<void> } {
  const [unreadCount, setUnreadCount] = useState(0)
  const { user } = useAuth()
  const userIdRef = useRef<string | null>(null)
  userIdRef.current = user?.id ?? null

  // tenantId atual em ref pra fetchCount enxergar valor fresco sem
  // recriar a função (estabilidade do useCallback / subscribe Realtime).
  const tenantIdRef = useRef<string | null | undefined>(tenantId)
  tenantIdRef.current = tenantId

  const fetchCount = useCallback(async () => {
    if (isMockMode || !userIdRef.current) {
      setUnreadCount(0)
      return
    }
    try {
      const { data, error } = await supabase.rpc("count_unread_per_conversation_for_user")
      if (error) {
        console.warn("useUnreadMessagesCount RPC erro:", error.message)
        return
      }
      if (!Array.isArray(data)) return
      const rows = data as Array<{ conversation_id: string; unread_count: number }>

      // Sem filtro de tenant: comportamento original (soma global).
      const activeTenantId = tenantIdRef.current
      if (!activeTenantId) {
        const total = rows.reduce((sum, row) => sum + (row.unread_count ?? 0), 0)
        setUnreadCount(total)
        return
      }

      // Com filtro de tenant (task #161): pega lista de conversation_ids do
      // tenant ativo e soma apenas as linhas da RPC que casam. Em vez de
      // baixar todas as conversas (que vem com payload grande), pedimos só
      // o `id` — leve o suficiente pra ser feito a cada refetch.
      const { data: tenantConvs, error: convErr } = await supabase
        .from("credit_v2_conversations")
        .select("id")
        .eq("tenant_id", activeTenantId)

      if (convErr) {
        console.warn("useUnreadMessagesCount lista de conversas do tenant erro:", convErr.message)
        // Fallback conservador: não calcula nada (mantém 0 ao invés de mostrar dado errado).
        setUnreadCount(0)
        return
      }

      const tenantConvIds = new Set(
        ((tenantConvs ?? []) as Array<{ id: string }>).map((c) => c.id),
      )
      const total = rows
        .filter((row) => tenantConvIds.has(row.conversation_id))
        .reduce((sum, row) => sum + (row.unread_count ?? 0), 0)
      setUnreadCount(total)
    } catch (err) {
      console.warn("useUnreadMessagesCount erro:", err)
    }
  }, [])

  useEffect(() => {
    if (isMockMode || !user?.id) {
      setUnreadCount(0)
      return
    }

    void fetchCount()

    // Realtime: novas mensagens (incrementam) ou updates em membership
    // (zeram quando user abre conversa). Em ambos os casos, refetch.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const channel = (supabase.channel(`unread_messages:${user.id}`) as any)
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "credit_v2_messages" },
        () => { void fetchCount() },
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "credit_v2_conversation_members", filter: `user_id=eq.${user.id}` },
        () => { void fetchCount() },
      )
      .subscribe()

    return () => { void supabase.removeChannel(channel) }
  }, [user?.id, fetchCount])

  // Quando o tenant ativo muda, refetch imediatamente pra refletir o novo
  // escopo (não esperar próximo evento Realtime).
  useEffect(() => {
    if (isMockMode || !user?.id) return
    void fetchCount()
  }, [tenantId, user?.id, fetchCount])

  return { unreadCount, refetch: fetchCount }
}
